import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useTracks,
  useRoomContext,
  VideoTrack,
} from "@livekit/components-react";
import { Track, RoomEvent } from "livekit-client";
import "@livekit/components-styles";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { toast } from "sonner";
import { X, Send, Users, Gift, Radio, Sparkles, Volume2, VolumeX, LayoutGrid, Mic, MicOff, SwitchCamera, MoreHorizontal } from "lucide-react";
import LiveGiftRain from "@/components/LiveGiftRain";
import LiveLeaderboard from "@/components/LiveLeaderboard";
import LiveYouTubePlayer from "@/components/LiveYouTubePlayer";
import LiveYouTubeLauncher from "@/components/LiveYouTubeLauncher";
import LiveRecorder from "@/components/LiveRecorder";
import LiveScreenShare from "@/components/LiveScreenShare";
import LiveSeatQueue from "@/components/LiveSeatQueue";
import { isGiftSfxEnabled, setGiftSfxEnabled, primeGiftSfx } from "@/lib/giftSounds";

const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL;

// Beauty-filter constants — mirrored from LivePreflight so the creator's
// preferred smoothing carries over from the pre-flight preview into the
// live room. CSS filters only affect the local <video> element rendering,
// so this is a "how the creator sees themselves" thing rather than
// something viewers see. A true broadcast-side filter needs a Canvas
// pipeline (future work).
const BEAUTY_KEY = "neosync:live:beauty";
const BEAUTY_FILTER = "blur(0.5px) contrast(1.08) saturate(1.1) brightness(1.05)";
function readBeautyPref() {
  try { return localStorage.getItem(BEAUTY_KEY) === "1"; }
  catch { return false; }
}

/**
 * MatrixShopping live room — one component serves both broadcaster (?host=1)
 * and viewers. Wraps `<LiveKitRoom>` which handles the WebRTC pub/sub
 * behind the scenes.
 */
export default function LiveRoom() {
  const { roomName } = useParams();
  const [params] = useSearchParams();
  const isHost = params.get("host") === "1";
  const [session, setSession] = useState(null); // { token, server_url }
  const [meta, setMeta] = useState(null);       // stream row
  const [error, setError] = useState(null);
  const [isGuestPublisher, setIsGuestPublisher] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { subscribe } = useWS();

  // Fetch join token + room metadata in parallel.
  useEffect(() => {
    // Wait for the auth check to settle before deciding to bounce to
    // login. Without this we redirect on the very first render (before
    // /auth/me completes) even for logged-in users.
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    let alive = true;
    (async () => {
      try {
        const [tokenResp, metaResp] = await Promise.all([
          isHost
            ? Promise.resolve(null) // host token was returned by /live/start; will fetch via join fallback
            : api.post(`/live/${roomName}/join`),
          api.get(`/live/${roomName}`),
        ]);
        if (!alive) return;
        setMeta(metaResp.data);
        if (isHost) {
          // Host tokens are always fresh from /start — but if we landed here
          // via a hard refresh, the token isn't in memory, so hit /start
          // which returns the existing room's publisher token (idempotent).
          const { data } = await api.post("/live/start", {
            title: metaResp.data?.title || "",
            allow_battle: metaResp.data?.allow_battle || false,
          });
          setSession({ token: data.token, server_url: data.server_url });
        } else {
          setSession({
            token: tokenResp.data.token,
            server_url: tokenResp.data.server_url,
          });
        }
      } catch (err) {
        if (!alive) return;
        setError(err?.response?.data?.detail || "Could not open this live stream");
      }
    })();
    return () => { alive = false; };
  }, [roomName, isHost, user, authLoading, navigate]);

  const handleDisconnected = useCallback(() => {
    navigate("/live");
  }, [navigate]);

  // Guest-publisher upgrade path.  When a viewer's seat request is
  // approved, the backend fires `live:seat_approved`.  We call
  // `/battle/accept` for a fresh publish-capable token, swap it into
  // `session`, refresh the room meta (so `battle_participants` includes
  // this user), and let the `key={session.token}` on <LiveKitRoom>
  // trigger a full reconnect with publish permissions + mic/cam on.
  useEffect(() => {
    if (!subscribe || !roomName) return;
    const off = subscribe(async (msg) => {
      if (msg?.type !== "live:seat_approved" || msg.room_name !== roomName) return;
      if (isHost) return;    // hosts never need to upgrade
      try {
        const [{ data: joinData }, { data: freshMeta }] = await Promise.all([
          api.post(`/live/${roomName}/battle/accept`),
          api.get(`/live/${roomName}`),
        ]);
        setMeta(freshMeta);
        setSession({
          token: joinData.token,
          server_url: joinData.server_url,
        });
        setIsGuestPublisher(true);
        toast.success("You're on stage — camera + mic are live");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't take the seat");
      }
    });
    return off;
  }, [subscribe, roomName, isHost]);

  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-[#FF007F] font-mono text-sm">// {error}</p>
        <button
          onClick={() => navigate("/live")}
          className="px-4 py-2 border border-white/20 text-white/80 text-xs font-mono uppercase tracking-widest hover:bg-white/10"
          data-testid="live-back-btn"
        >
          Back to Live
        </button>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white/50 font-mono text-sm animate-pulse">// connecting.to.room…</p>
      </div>
    );
  }

  return (
    <LiveKitRoom
      key={session.token}
      serverUrl={session.server_url || LIVEKIT_URL}
      token={session.token}
      connect
      audio={isHost || isGuestPublisher}
      video={isHost || isGuestPublisher}
      onDisconnected={handleDisconnected}
      data-lk-theme="default"
      style={{ height: "100vh" }}
    >
      <LiveInterior
        roomName={roomName}
        isHost={isHost}
        isGuestPublisher={isGuestPublisher}
        meta={meta}
        onEnd={handleDisconnected}
      />
      {/* RoomAudioRenderer subscribes to and plays all remote audio
          tracks. Explicit volume=1 so a browser's default half-volume
          hidden-audio behaviour doesn't apply. */}
      <RoomAudioRenderer volume={1} />
      {/* Autoplay-policy fallback — this component ONLY renders when
          the browser has blocked audio playback (e.g. iOS Safari on the
          very first tap). Tap it once and audio starts flowing. */}
      <StartAudio
        label="🔊 TAP TO UNMUTE STREAM"
        data-testid="live-start-audio"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 60,
          padding: "14px 28px",
          background: "#FF007F",
          color: "white",
          fontWeight: 900,
          letterSpacing: "0.15em",
          fontSize: "13px",
          border: "2px solid white",
          boxShadow: "0 0 40px rgba(255, 0, 127, 0.6)",
          cursor: "pointer",
        }}
      />
    </LiveKitRoom>
  );
}

// ---------------------------------------------------------------------------
// Interior — runs INSIDE <LiveKitRoom>, has access to useRoomContext(). Owns
// video render, chat, gift overlay, end-stream, viewer count, gift picker.
// ---------------------------------------------------------------------------
function LiveInterior({ roomName, isHost, isGuestPublisher, meta, onEnd }) {
  const room = useRoomContext();
  const { user } = useAuth();
  const [localMeta, setLocalMeta] = useState(meta);
  useEffect(() => { setLocalMeta(meta); }, [meta]);
  const effectiveMeta = localMeta || meta;
  const tracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [floatingGifts, setFloatingGifts] = useState([]);
  const [showGifts, setShowGifts] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [rainGift, setRainGift] = useState(null);   // triggers full-screen shower
  const [leaderboardTick, setLeaderboardTick] = useState(0);
  const [beauty, setBeauty] = useState(readBeautyPref);
  const [sfxOn, setSfxOn] = useState(isGiftSfxEnabled);
  const [ytState, setYtState] = useState(null);
  const [seatRequesting, setSeatRequesting] = useState(false);
  const [seatQueueTick, setSeatQueueTick] = useState(0);
  const [viewerCount, setViewerCount] = useState(meta?.viewer_count || 0);
  const [micOn, setMicOn] = useState(true);   // publishers only — reflects LiveKit track state
  const [facingMode, setFacingMode] = useState("user"); // "user" | "environment"
  const [videoDevices, setVideoDevices] = useState([]);   // {deviceId, label}
  const [activeCamId, setActiveCamId] = useState(null);

  // Pull the current YouTube state on join (late-joiners get last-known
  // playback position + video ID from the room row).
  useEffect(() => { setYtState(meta?.youtube || null); }, [meta?.youtube]);
  const chatEndRef = useRef(null);

  // Load gift catalog
  useEffect(() => {
    api.get("/live/gifts/catalog").then(({ data }) => setGifts(data.gifts || []));
  }, []);

  // Track viewer count from LiveKit participants (SDK gives us live count
  // even without waiting for the webhook to sync).
  useEffect(() => {
    if (!room) return;
    const sync = () => {
      // participants + local. Exclude broadcaster from viewer count.
      const total = room.remoteParticipants.size + 1; // +1 for local
      const audience = isHost ? total - 1 : total - 1; // subtract broadcaster
      setViewerCount(Math.max(0, audience));
    };
    sync();
    room.on(RoomEvent.ParticipantConnected, sync);
    room.on(RoomEvent.ParticipantDisconnected, sync);
    return () => {
      room.off(RoomEvent.ParticipantConnected, sync);
      room.off(RoomEvent.ParticipantDisconnected, sync);
    };
  }, [room, isHost]);

  // Publisher-only mic state — mirror the actual LiveKit local mic
  // enabled state so an outside toggle (e.g. beauty-cam pipeline) can't
  // desync the UI. Only publishers have a mic track to mute, so we skip
  // subscribing for pure viewers.
  useEffect(() => {
    if (!room) return;
    const lp = room.localParticipant;
    if (!lp) return;
    const sync = () => setMicOn(!!lp.isMicrophoneEnabled);
    sync();
    lp.on("trackMuted", sync);
    lp.on("trackUnmuted", sync);
    lp.on("trackPublished", sync);
    return () => {
      lp.off("trackMuted", sync);
      lp.off("trackUnmuted", sync);
      lp.off("trackPublished", sync);
    };
  }, [room]);

  const toggleMic = useCallback(async () => {
    if (!room) return;
    const lp = room.localParticipant;
    const next = !lp.isMicrophoneEnabled;
    try {
      await lp.setMicrophoneEnabled(next);
      setMicOn(next);
    } catch (err) {
      toast.error(err?.message || "Couldn't toggle mic");
    }
  }, [room]);

  // Enumerate video-input devices so the flip button can cycle through
  // them by explicit deviceId (the reliable path on desktop, where
  // facingMode is undefined for USB webcams). Also captures the
  // currently-active deviceId so we know where in the cycle we are.
  useEffect(() => {
    if (!room) return;
    const publishing = isHost || isGuestPublisher;
    if (!publishing) return;
    if (!navigator.mediaDevices?.enumerateDevices) return;

    let alive = true;
    const refresh = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        if (!alive) return;
        const vids = all
          .filter((d) => d.kind === "videoinput" && d.deviceId)
          .map((d) => ({ deviceId: d.deviceId, label: d.label || "Camera" }));
        setVideoDevices(vids);
        const lp = room.localParticipant;
        const camPub = lp.getTrackPublication?.(Track.Source.Camera)
          || Array.from(lp.videoTrackPublications?.values?.() || [])
            .find((p) => p.source === Track.Source.Camera);
        const currentDev = (camPub?.videoTrack || camPub?.track)
          ?.mediaStreamTrack?.getSettings?.().deviceId;
        setActiveCamId(currentDev || vids[0]?.deviceId || null);
      } catch { /* enumerate failed — skip */ }
    };
    refresh();
    navigator.mediaDevices.addEventListener?.("devicechange", refresh);
    return () => {
      alive = false;
      navigator.mediaDevices.removeEventListener?.("devicechange", refresh);
    };
  }, [room, isHost, isGuestPublisher]);

  const flipCamera = useCallback(async () => {
    if (!room) return;
    const lp = room.localParticipant;
    const camPub = lp.getTrackPublication?.(Track.Source.Camera)
      || Array.from(lp.videoTrackPublications?.values?.() || [])
        .find((p) => p.source === Track.Source.Camera);
    const track = camPub?.videoTrack || camPub?.track;
    if (!track) {
      toast.error("Camera isn't publishing yet");
      return;
    }

    // Primary path: explicit deviceId cycling. Desktop browsers (and
    // desktop USB webcams) don't populate `facingMode`, so relying on
    // it there is unreliable; deviceIds are always distinct.
    if (videoDevices.length >= 2) {
      const idx = Math.max(
        0,
        videoDevices.findIndex((d) => d.deviceId === activeCamId),
      );
      const target = videoDevices[(idx + 1) % videoDevices.length];
      try {
        await track.restartTrack({ deviceId: { exact: target.deviceId } });
        setActiveCamId(target.deviceId);
        // Refresh facingMode from settings so the mobile fallback stays
        // coherent when someone flips back on desktop.
        const newFacing = track.mediaStreamTrack?.getSettings?.().facingMode;
        if (newFacing) setFacingMode(newFacing);
        const isRear = /back|rear|environment/i.test(target.label);
        const isFront = /front|user|self/i.test(target.label);
        toast.success(isRear ? "Rear camera" : isFront ? "Front camera" : "Camera flipped");
        return;
      } catch { /* fall through to facingMode fallback */ }
    }

    // Fallback path: mobile browsers often expose 0 or 1 deviceId until
    // permission is granted per-sensor, so cycling by deviceId is
    // impossible. Use `facingMode` to hop to the other side of the phone.
    const nextFacing = facingMode === "environment" ? "user" : "environment";
    try {
      await track.restartTrack({ facingMode: nextFacing });
      setFacingMode(nextFacing);
      toast.success(nextFacing === "environment" ? "Rear camera" : "Front camera");
    } catch (err) {
      toast.error(err?.message || "Couldn't flip camera");
    }
  }, [room, videoDevices, activeCamId, facingMode]);

  // Chat + gift data-channel receiver.
  useEffect(() => {
    if (!room) return;
    const onData = (data, participant, _kind, topic) => {
      try {
        const decoded = new TextDecoder().decode(data);
        const payload = JSON.parse(decoded);
        if (topic === "chat") {
          setMessages((m) => [
            ...m.slice(-99),
            {
              id: `${participant?.identity}-${Date.now()}-${Math.random()}`,
              from: payload.name || participant?.name || "guest",
              text: String(payload.text || "").slice(0, 200),
            },
          ]);
        } else if (topic === "gift" || payload.kind === "gift") {
          setFloatingGifts((g) => [
            ...g,
            {
              id: `${Date.now()}-${Math.random()}`,
              icon: payload.icon,
              label: payload.label,
              coins: payload.coins,
              sender: payload.sender_name || "someone",
            },
          ]);
          // Auto-fade
          setTimeout(() => {
            setFloatingGifts((g) => g.slice(1));
          }, 4000);

          // High-tier gifts trigger the full-screen coin shower. We use
          // 500 as the floor (Rocket) — anything smaller stays as the
          // little floating pop-up. The `id` gives React a stable key so
          // rapid back-to-back mega-gifts replace cleanly.
          if ((payload.coins || 0) >= 500) {
            setRainGift({
              id: `rain-${Date.now()}-${Math.random()}`,
              icon: payload.icon,
              label: payload.label,
              coins: payload.coins,
              sender: payload.sender_name || "someone",
            });
          }
          // Nudge the leaderboard to refresh — any gift of any size can
          // shift the top-3 ranks.
          setLeaderboardTick((t) => t + 1);
        } else if (topic === "youtube" || payload.kind === "youtube") {
          // Sync YouTube state from the host across every participant.
          setYtState({
            video_id: payload.video_id,
            action: payload.action,
            position: payload.position || 0,
            updated_at: payload.updated_at || new Date().toISOString(),
            controller_id: payload.controller_id,
          });
        } else if (topic === "meta" || payload.kind === "multiguest") {
          // Host flipped the multiguest toggle — every viewer needs
          // their `seatMode` + grid size to update instantly.
          setLocalMeta((m) => ({
            ...(m || {}),
            allow_battle: !!payload.allow_battle,
            seat_capacity: payload.seat_capacity ?? (payload.allow_battle ? 12 : 0),
          }));
        }
      } catch { /* bad packet, ignore */ }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => { room.off(RoomEvent.DataReceived, onData); };
  }, [room]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const sendChat = useCallback(async () => {
    const t = chatInput.trim();
    if (!t) return;
    try {
      await room.localParticipant.publishData(
        new TextEncoder().encode(JSON.stringify({
          text: t.slice(0, 200),
          name: user?.name || user?.username || "guest",
        })),
        { reliable: true, topic: "chat" },
      );
      setMessages((m) => [
        ...m.slice(-99),
        { id: `me-${Date.now()}`, from: "you", text: t, mine: true },
      ]);
      setChatInput("");
    } catch (e) {
      toast.error("Chat failed — reconnect?");
    }
  }, [chatInput, room, user]);

  const sendGift = useCallback(async (giftKey) => {
    try {
      const { data } = await api.post(`/live/${roomName}/gift`, { gift_key: giftKey });
      toast.success(`${data.gift.icon} sent — ${data.new_balance} coins left`);
      setShowGifts(false);
      // Bump leaderboard immediately so the sender sees their new rank
      // without waiting for the fan-out data-packet to loopback.
      setLeaderboardTick((t) => t + 1);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Gift failed";
      toast.error(msg);
    }
  }, [roomName]);

  const endStream = useCallback(async () => {
    if (!isHost) { room.disconnect(); onEnd(); return; }
    if (!window.confirm("End this live stream?")) return;
    try {
      await api.post(`/live/${roomName}/end`);
    } catch { /* proceed regardless */ }
    room.disconnect();
    onEnd();
  }, [isHost, room, roomName, onEnd]);

  const videoTracks = useMemo(() => {
    return tracks.filter((t) => t.publication?.track);
  }, [tracks]);

  // Bigo-style multi-seat layout when the room has an active
  // `seat_capacity`. Falls back to the legacy `allow_battle` bool (=
  // 12) for rooms that predate the picker. Non-battle rooms just
  // render publishers in a natural 1-2 column grid.
  const seatCapacity = Number(effectiveMeta?.seat_capacity) ||
    (effectiveMeta?.allow_battle ? 12 : 0);
  const seatMode = seatCapacity > 0;
  const emptySeats = seatMode ? Math.max(0, seatCapacity - videoTracks.length) : 0;
  // Grid geometry per capacity — tuned so tiles stay roughly square:
  //   4  -> 2 x 2
  //   6  -> 2 x 3 (2 cols, 3 rows on mobile / portrait)
  //   9  -> 3 x 3
  //   12 -> 4 x 3
  const seatGridClass = ({
    4:  "grid-cols-2 grid-rows-2",
    6:  "grid-cols-2 grid-rows-3",
    9:  "grid-cols-3 grid-rows-3",
    12: "grid-cols-4 grid-rows-3",
  }[seatCapacity]) || "grid-cols-4 grid-rows-3";

  const [seatPickerOpen, setSeatPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);   // collapsible top-bar controls

  async function setMultiguestCapacity(seats) {
    setSeatPickerOpen(false);
    try {
      const { data } = await api.post(`/live/${roomName}/toggle-multiguest`, { seats });
      setLocalMeta((m) => ({
        ...(m || {}),
        allow_battle: data.allow_battle,
        seat_capacity: data.seat_capacity,
      }));
      toast.success(
        data.seat_capacity
          ? `Multiguest ON — ${data.seat_capacity - 1} guest seats`
          : "Multiguest OFF",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change seats");
    }
  }

  async function requestSeat() {
    setSeatRequesting(true);
    try {
      await api.post(`/live/${roomName}/battle/request-seat`);
      toast.success("Seat requested — waiting for host to approve");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't request seat");
    } finally {
      setSeatRequesting(false);
    }
  }

  const kickGuest = useCallback(async (targetId, targetName) => {
    if (!isHost) return;
    const label = targetName ? `@${targetName}` : "this guest";
    if (!window.confirm(`Kick ${label} off the stage?`)) return;
    try {
      await api.post(`/live/${roomName}/battle/kick`, {
        invitee_user_id: targetId,
      });
      // Optimistically drop them from local meta so the seat opens up
      // right away — the LiveKit participant_left event will follow.
      setLocalMeta((m) => ({
        ...(m || {}),
        battle_participants: (m?.battle_participants || []).filter(
          (id) => id !== targetId,
        ),
      }));
      toast.success(`${label} kicked`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't kick guest");
    }
  }, [isHost, roomName]);

  const isCoHost = isHost || isGuestPublisher || (effectiveMeta?.battle_participants || []).includes(user?.user_id);

  // Listen for seat-request events from other viewers so the host's
  // queue panel re-renders instantly (rather than waiting for its 20s
  // poll).  Guest-side "seat_approved" is handled in the parent
  // <LiveRoom> so it can swap the LiveKit token in place.
  const { subscribe } = useWS();
  useEffect(() => {
    if (!subscribe) return;
    const off = subscribe((msg) => {
      if (msg?.type === "live:seat_request" && msg.room_name === roomName) {
        setSeatQueueTick((t) => t + 1);
      }
    });
    return off;
  }, [subscribe, roomName]);

  return (
    <div className="relative w-full h-screen bg-black text-white overflow-hidden">
      {/* Full-screen coin shower for Rocket/Crown/Galaxy tier gifts.
          Rendered at the top so it sits above every other overlay while
          still respecting the LiveKit video underneath. */}
      <LiveGiftRain activeGift={rainGift} onDone={() => setRainGift(null)} />

      {/* Top-gifter leaderboard — floats top-left, refreshes on every
          incoming gift + a 25s poll safety-net. */}
      <LiveLeaderboard roomName={roomName} refreshTrigger={leaderboardTick} />

      {/* Host-only seat-request queue — tap-to-approve / tap-to-deny
          panel that appears whenever there are pending requests. */}
      {isHost && (
        <LiveSeatQueue
          roomName={roomName}
          refreshTrigger={seatQueueTick}
          seatMode={seatMode}
        />
      )}

      {/* Shared YouTube co-watch player (Bigo-style). Host + co-hosts
          drive it, viewers auto-sync via the "youtube" data-channel. */}
      <LiveYouTubePlayer
        roomName={roomName}
        ytState={ytState}
        canControl={isCoHost}
        myUserId={user?.user_id}
        room={room}
      />

      {/* Video grid — Bigo multi-seat layout when multiguest is on
          (grid geometry adapts to seat_capacity 4/6/9/12), otherwise a
          natural 1-2 column publisher grid. When a YouTube co-watch is
          running, the grid is pushed below the shared-video band so
          it never overlays the publishers' faces. */}
      <div
        className={
          ytState?.video_id
            ? (seatMode
                ? `absolute left-0 right-0 bottom-0 top-[calc(3.5rem+40vh)] md:top-[calc(3.5rem+45vh)] grid ${seatGridClass} gap-1 p-1`
                : `absolute left-0 right-0 bottom-0 top-[calc(3.5rem+40vh)] md:top-[calc(3.5rem+45vh)] grid ${videoTracks.length >= 2 ? "grid-cols-2" : "grid-cols-1"}`)
            : (seatMode
                ? `absolute inset-0 grid ${seatGridClass} gap-1 p-1`
                : `absolute inset-0 grid ${videoTracks.length >= 2 ? "grid-cols-2" : "grid-cols-1"}`)
        }
      >
        {videoTracks.length === 0 && !seatMode && (
          <div className="flex items-center justify-center">
            <p className="text-white/40 font-mono text-sm animate-pulse">
              // waiting.for.publisher…
            </p>
          </div>
        )}
        {videoTracks.map((t) => {
          const applyBeauty = beauty && t.participant.isLocal && (isHost || isCoHost);
          const isScreen = t.source === "screen_share" || t.publication?.source === "screen_share";
          const tileIdentity = t.participant.identity;
          const tileIsHost = tileIdentity === effectiveMeta?.broadcaster_id;
          const canKickTile = isHost && !tileIsHost && !isScreen;
          return (
            <div key={`${t.participant.identity}-${t.publication?.trackSid}`} className="relative bg-black overflow-hidden">
              <VideoTrack
                trackRef={t}
                className={`w-full h-full ${isScreen ? "object-contain bg-black" : "object-cover"} transition-[filter] duration-300`}
                style={applyBeauty && !isScreen ? { filter: BEAUTY_FILTER } : undefined}
              />
              <span className="absolute bottom-1 left-1 bg-black/70 backdrop-blur-sm text-[10px] font-mono px-1.5 py-0.5 border border-white/10 truncate max-w-[90%]">
                @{t.participant.name || t.participant.identity}
              </span>
              {isScreen && (
                <span className="absolute top-1 left-1 bg-[#39FF14] text-black text-[10px] font-black px-1.5 py-0.5 font-mono">
                  SCREEN
                </span>
              )}
              {canKickTile && (
                <button
                  type="button"
                  onClick={() => kickGuest(tileIdentity, t.participant.name)}
                  data-testid={`live-kick-${tileIdentity}`}
                  aria-label={`Kick @${t.participant.name || tileIdentity} off the stage`}
                  title="Kick this guest off the stage"
                  className="absolute top-1 right-1 bg-black/70 hover:bg-[#FF007F] text-white p-1 border border-white/20 hover:border-[#FF007F] transition-colors backdrop-blur-sm"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
        {/* Tap-to-request seat placeholders (Bigo pattern). Viewers tap
            any `+` tile to ask the host for that seat. Host doesn't see
            the tap-target (they're already on a seat) so we render a
            plain `+` for them instead. */}
        {seatMode && Array.from({ length: emptySeats }).map((_, i) => {
          const seatNumber = videoTracks.length + i + 1;
          if (isCoHost) {
            return (
              <div
                key={`empty-${i}`}
                className="relative bg-white/5 border border-white/10 flex items-center justify-center text-white/30"
                data-testid={`live-empty-seat-${i}`}
              >
                <span className="absolute top-1 left-1 text-[10px] font-mono text-white/40">
                  {seatNumber}
                </span>
                <span className="text-3xl">+</span>
              </div>
            );
          }
          return (
            <button
              key={`empty-${i}`}
              type="button"
              onClick={requestSeat}
              disabled={seatRequesting}
              className="relative bg-white/5 hover:bg-[#39FF14]/15 active:bg-[#39FF14]/25 border border-white/10 hover:border-[#39FF14] flex flex-col items-center justify-center text-white/40 hover:text-[#39FF14] transition-colors group disabled:opacity-50"
              data-testid={`live-empty-seat-${i}`}
              title="Tap to request this seat"
              aria-label={`Request seat ${seatNumber}`}
            >
              <span className="absolute top-1 left-1 text-[10px] font-mono">
                {seatNumber}
              </span>
              <span className="text-3xl group-hover:scale-125 transition-transform">
                {seatRequesting ? "…" : "+"}
              </span>
              <span className="text-[9px] font-mono uppercase tracking-widest mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                tap to join
              </span>
            </button>
          );
        })}
      </div>

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-40 p-3 flex items-center justify-between gap-2 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-2 min-w-0 shrink">
          <span className="bg-[#FF007F] text-white text-[10px] font-black px-2 py-1 flex items-center gap-1 shrink-0">
            <Radio className="w-3 h-3" /> LIVE
          </span>
          <span className="bg-black/60 backdrop-blur-sm text-[10px] font-mono px-2 py-1 flex items-center gap-1 border border-white/10 shrink-0">
            <Users className="w-3 h-3" /> <span data-testid="live-viewer-count">{viewerCount}</span>
          </span>
          <span className="hidden md:inline-block text-xs font-bold truncate max-w-[30vw]">
            {effectiveMeta?.title || "Live stream"}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 min-w-0">
          {menuOpen && isHost && (
            <div className="relative shrink-0">
              <button
                onClick={() => setSeatPickerOpen((v) => !v)}
                data-testid="live-toggle-multiguest"
                aria-haspopup="true"
                aria-expanded={seatPickerOpen}
                aria-pressed={seatMode}
                title={seatMode ? `Multiguest: ${seatCapacity} seats` : "Enable multiguest"}
                className={`p-2 transition-colors ${seatMode ? "bg-[#39FF14] text-black shadow-[0_0_16px_rgba(57,255,20,0.55)]" : "bg-white/10 hover:bg-white/20 text-white/80"}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              {seatPickerOpen && (
                <>
                  {/* Click-outside catcher */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setSeatPickerOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute right-0 top-full mt-2 z-50 bg-[#0A0A0C] border-2 border-[#39FF14] shadow-[0_0_40px_rgba(57,255,20,0.35)] min-w-[10rem]"
                    role="menu"
                    data-testid="live-seat-picker"
                  >
                    <div className="px-3 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/60">
                      seat.capacity
                    </div>
                    {[4, 6, 9, 12].map((n) => (
                      <button
                        key={n}
                        onClick={() => setMultiguestCapacity(n)}
                        data-testid={`live-seat-option-${n}`}
                        className={`w-full px-3 py-2 text-left text-xs font-black uppercase tracking-widest transition-colors flex items-center justify-between ${
                          seatCapacity === n
                            ? "bg-[#39FF14]/20 text-[#39FF14]"
                            : "text-white/80 hover:bg-white/10"
                        }`}
                      >
                        <span>{n} seats</span>
                        <span className="text-[10px] font-mono text-white/50">
                          host + {n - 1}
                        </span>
                      </button>
                    ))}
                    <button
                      onClick={() => setMultiguestCapacity(0)}
                      data-testid="live-seat-option-off"
                      className={`w-full px-3 py-2 text-left text-xs font-black uppercase tracking-widest border-t border-white/10 transition-colors ${
                        !seatMode
                          ? "bg-[#FF007F]/20 text-[#FF007F]"
                          : "text-white/60 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      Turn off
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {menuOpen && isCoHost && (
            <button
              onClick={toggleMic}
              data-testid="live-mic-toggle"
              aria-pressed={!micOn}
              aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
              title={micOn ? "Mic on — tap to mute" : "Mic muted — tap to unmute"}
              className={`p-2 transition-colors ${
                micOn
                  ? "bg-white/10 hover:bg-white/20 text-white/80"
                  : "bg-[#FF007F] hover:bg-[#FF3399] text-white shadow-[0_0_16px_rgba(255,0,127,0.55)]"
              }`}
            >
              {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            </button>
          )}
          {menuOpen && isCoHost && videoDevices.length >= 2 && (
            <button
              onClick={flipCamera}
              data-testid="live-camera-flip"
              aria-label="Flip camera"
              title="Flip between front and back camera"
              className="p-2 bg-white/10 hover:bg-white/20 text-white/80 transition-colors"
            >
              <SwitchCamera className="w-4 h-4" />
            </button>
          )}
          {menuOpen && isCoHost && <LiveScreenShare />}
          {menuOpen && isCoHost && <LiveRecorder />}
          {menuOpen && isCoHost && (
            <LiveYouTubeLauncher
              roomName={roomName}
              active={!!ytState?.video_id}
            />
          )}
          {seatMode && !isCoHost && (
            <button
              onClick={requestSeat}
              disabled={seatRequesting}
              data-testid="live-request-seat"
              className="px-3 py-1.5 bg-[#39FF14] hover:bg-[#5EFF3D] text-black font-black text-[10px] uppercase tracking-widest disabled:opacity-50 shrink-0"
              title="Ask the host for a seat on the multiguest grid"
            >
              {seatRequesting ? "…" : "+ seat"}
            </button>
          )}
          {menuOpen && (
            <button
              onClick={() => {
                setSfxOn((s) => {
                  const next = !s;
                  setGiftSfxEnabled(next);
                  if (next) primeGiftSfx();  // unlock the AudioContext on toggle-on
                  return next;
                });
              }}
              data-testid="live-sfx-toggle"
              className={`p-2 transition-colors ${sfxOn ? "bg-white/10 text-white/80 hover:bg-white/20" : "bg-white/5 text-white/40 hover:bg-white/10"}`}
              aria-pressed={sfxOn}
              aria-label={sfxOn ? "Mute gift sounds" : "Unmute gift sounds"}
              title={sfxOn ? "Gift sounds on — tap to mute" : "Gift sounds muted — tap to enable"}
            >
              {sfxOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          )}
          {menuOpen && isHost && (
            <button
              onClick={() => {
                setBeauty((b) => {
                  const next = !b;
                  try { localStorage.setItem(BEAUTY_KEY, next ? "1" : "0"); } catch { /* private mode */ }
                  return next;
                });
              }}
              data-testid="live-beauty-toggle"
              className={`p-2 transition-colors flex items-center gap-1 ${beauty ? "bg-[#FF007F] text-white shadow-[0_0_16px_rgba(255,0,127,0.55)]" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
              aria-pressed={beauty}
              aria-label={beauty ? "Turn off beauty filter" : "Turn on beauty filter"}
              title="Beauty filter (only affects your own view)"
            >
              <Sparkles className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="live-menu-toggle"
            aria-expanded={menuOpen}
            aria-label={menuOpen ? "Hide controls" : "Show controls"}
            title={menuOpen ? "Hide controls" : "Show more controls"}
            className={`shrink-0 p-2 transition-colors ${
              menuOpen
                ? "bg-[#39FF14] text-black shadow-[0_0_16px_rgba(57,255,20,0.55)]"
                : "bg-white/10 hover:bg-white/20 text-white/80"
            }`}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          <button
            onClick={endStream}
            data-testid="live-end-btn"
            className="shrink-0 bg-white/10 hover:bg-[#FF007F] text-white p-2 transition-colors"
            aria-label={isHost ? "End stream" : "Leave"}
            title={isHost ? "End stream" : "Leave the room"}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Floating gift animations */}
      <div className="pointer-events-none absolute inset-x-0 top-1/4 z-20 flex flex-col items-center gap-3">
        {floatingGifts.map((g) => (
          <div
            key={g.id}
            className="animate-live-gift-pop bg-black/60 backdrop-blur-md border border-[#FF007F] px-4 py-2 flex items-center gap-2 shadow-[0_0_30px_rgba(255,0,127,0.5)]"
          >
            <span className="text-3xl">{g.icon}</span>
            <div className="text-left">
              <p className="text-xs font-bold text-white">{g.sender} sent {g.label}</p>
              <p className="text-[10px] font-mono text-[#39FF14]">+{g.coins} coins → broadcaster</p>
            </div>
          </div>
        ))}
      </div>

      {/* Chat panel — bottom-anchored on mobile (sits ABOVE the input
          bar so messages never hide behind it), side panel on desktop.
          The `bottom-20` on mobile leaves ~5rem clearance for the input
          bar underneath. */}
      <div className="absolute bottom-20 inset-x-0 md:right-0 md:top-16 md:bottom-24 md:w-80 md:left-auto md:bottom-24 z-10 flex flex-col p-3 gap-2 pointer-events-none">
        <div className="flex-1 overflow-y-auto space-y-1 pointer-events-auto max-h-[38vh] md:max-h-full">
          {messages.slice(-30).map((m) => (
            <div key={m.id} className="text-xs bg-black/60 backdrop-blur-sm px-2 py-1 border-l-2 border-[#FF007F]/60 max-w-[85%]">
              <span className={`font-bold mr-1 ${m.mine ? "text-[#39FF14]" : "text-[#FF007F]"}`}>
                {m.from}:
              </span>
              <span className="text-white/90">{m.text}</span>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Bottom input bar — pinned to the true bottom of the viewport
          (safe-area padding so iOS home-indicator doesn't eat the
          buttons). Wide enough on mobile that the gift button is always
          visible next to the send button. */}
      <div
        className="absolute bottom-0 inset-x-0 z-30 p-3 bg-gradient-to-t from-black/95 via-black/70 to-transparent"
        style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center gap-2 max-w-2xl mx-auto">
          <input
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
            placeholder="Say something…"
            maxLength={200}
            data-testid="live-chat-input"
            className="flex-1 min-w-0 bg-black/60 backdrop-blur-md border border-white/10 focus:border-[#39FF14] text-white text-sm px-3 py-2 outline-none font-mono"
          />
          <button
            onClick={sendChat}
            data-testid="live-chat-send"
            className="shrink-0 bg-[#39FF14] hover:bg-[#5EFF3D] text-black p-2 font-bold"
            aria-label="Send chat message"
          >
            <Send className="w-4 h-4" />
          </button>
          {!isHost && (
            <button
              onClick={() => {
                primeGiftSfx();
                setShowGifts((v) => !v);
              }}
              data-testid="live-gift-toggle"
              className="shrink-0 bg-[#FF007F] hover:bg-[#FF3399] text-white p-2 font-bold shadow-[0_0_16px_rgba(255,0,127,0.5)]"
              aria-label="Send gift"
            >
              <Gift className="w-4 h-4" />
            </button>
          )}
        </div>
        {showGifts && !isHost && (
          <div className="max-w-2xl mx-auto mt-2 bg-[#0A0A0C] border border-[#FF007F]/40 p-3 max-h-[45vh] overflow-y-auto">
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#FF007F]" /> pick.a.gift
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
              {gifts.map((g) => (
                <button
                  key={g.key}
                  onClick={() => sendGift(g.key)}
                  data-testid={`live-gift-${g.key}`}
                  className="bg-black/60 border border-white/10 hover:border-[#39FF14] p-2 transition-colors text-center"
                >
                  <div className="text-2xl">{g.icon}</div>
                  <div className="text-[10px] font-mono text-white/80 truncate">{g.label}</div>
                  <div className="text-[10px] font-mono text-[#39FF14]">{g.coins}c</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
